import { VisitAnesthesiaFormModel, IVisitAnesthesiaFormModel, IVisitAnesthesiaFormPreOpModel, IVisitAnesthesiaFormSurgeryModel, IVisitAnesthesiaFormPostSurgeryModel, VisitAnesthesiaFormSurgeryVitalModel, VisitAnesthesiaFormPostSurgeryVitalModel } from "@common/models";
import { http } from "@common/utils";
import { trackPromise } from "react-promise-tracker";
import { ServiceHelper } from "./serviceHelper";

export class VisitAnesthesiaFormService {
    private static baseUrl: string = '/api/anesthesiamonitoringform';

    public static tags = {
        getVisitAnesthesiaForm: "getVisitAnesthesiaForm",
        upsertVisitAnesthesiaForm: "upsertVisitAnesthesiaForm",
        upsertVisitAnesthesiaFormPreOp: "getVisitAnesthesiaFormPreOp",
        upsertVisitAnesthesiaFormSurgery: "getVisitAnesthesiaFormSurgery",
        upsertVisitAnesthesiaFormPostSurgery: "getVisitAnesthesiaFormPostSurgery",
        upsertVisitAnesthesiaFormSurgeryVital: "getVisitAnesthesiaFormSurgeryVital",
        upsertVisitAnesthesiaFormPostSurgeryVital: "getVisitAnesthesiaFormPostSurgeryVital",
        deleteMedication: "deleteMedication",
    };

    static getVisitAnesthesiaFormById = (visitId: number, anesthesiaMonitoringFormId: number): Promise<VisitAnesthesiaFormModel> => {
        return trackPromise(new Promise<VisitAnesthesiaFormModel>((resolve, reject) => {
            http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}`)
                .get<IVisitAnesthesiaFormModel>()
                .then(data => { return new VisitAnesthesiaFormModel(data) })
                .then(resolve)
                .catch((error) => {
                    ServiceHelper.showError("Failed to get Anesthesia Form.", undefined)
                    reject(error);
                });
        }), this.tags.getVisitAnesthesiaForm);
    }


    static getVisitAnesthesiaFormByVisitId = (visitId: number): Promise<VisitAnesthesiaFormModel[]> =>
        trackPromise(new Promise<VisitAnesthesiaFormModel[]>((resolve, reject) => {
            http(`${this.baseUrl}/visit/${visitId}`)
                .get<IVisitAnesthesiaFormModel[]>()
                .then(data => data.map(d => new VisitAnesthesiaFormModel(d)))
                .then(resolve)
                .catch((error) => {
                    ServiceHelper.showError("Failed to get Anesthesia Form.", undefined)
                    reject(error);
                });
        }), this.tags.getVisitAnesthesiaForm)

    static upsertVisitAnesthesiaForm = (anesthesiaForm: VisitAnesthesiaFormModel): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(new Promise<VisitAnesthesiaFormModel>((resolve, reject) => {
            http(`${this.baseUrl}/`)
                .post<IVisitAnesthesiaFormModel>(anesthesiaForm)
                .then(data => new VisitAnesthesiaFormModel(data))
                .then(resolve)
                .catch((error) => {
                    ServiceHelper.showError("Failed to update Anesthesia Form.", undefined)
                    reject(error);
                });
        }), this.tags.upsertVisitAnesthesiaForm)

    static upsertVisitAnesthesiaFormPreOp = (visitId: number, anesthesiaMonitoringFormId: number, preOp: IVisitAnesthesiaFormPreOpModel): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(new Promise<VisitAnesthesiaFormModel>((resolve, reject) => {
            http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}/preop`)
                .post<IVisitAnesthesiaFormPreOpModel>(preOp)
                .then(data => new VisitAnesthesiaFormModel(data))
                .then(resolve)
                .catch(reject)
        }), this.tags.upsertVisitAnesthesiaFormPreOp)

    static upsertVisitAnesthesiaFormSurgery = (visitId: number, anesthesiaMonitoringFormId: number, surgery: IVisitAnesthesiaFormSurgeryModel): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(new Promise<VisitAnesthesiaFormModel>((resolve, reject) => {
            http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}/surgery`)
                .post<IVisitAnesthesiaFormSurgeryModel>(surgery)
                .then(data => new VisitAnesthesiaFormModel(data))
                .then(resolve)
                .catch((error) => {
                    ServiceHelper.showError("Failed to update Surgery.", undefined)
                    reject(error);
                });
        }), this.tags.upsertVisitAnesthesiaFormSurgery)

    static upsertVisitAnesthesiaFormPostSurgery = (visitId: number, anesthesiaMonitoringFormId: number, postSurgery: IVisitAnesthesiaFormPostSurgeryModel): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(new Promise<VisitAnesthesiaFormModel>((resolve, reject) => {
            http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}/postsurgery`)
                .post<IVisitAnesthesiaFormPostSurgeryModel>(postSurgery)
                .then(data => new VisitAnesthesiaFormModel(data))
                .then(resolve)
                .catch((error) => {
                    ServiceHelper.showError("Failed to update Post Surgery.", undefined)
                    reject(error);
                });
        }), this.tags.upsertVisitAnesthesiaFormPostSurgery)

    static upsertVisitAnesthesiaFormSurgeryVital = (visitId: number, anesthesiaMonitoringFormId: number, surgeryVital: VisitAnesthesiaFormSurgeryVitalModel): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(new Promise((resolve, reject) => {
            http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}/surgeryvital`)
                .post(surgeryVital)
                .then((data: IVisitAnesthesiaFormModel) => new VisitAnesthesiaFormModel(data))
                .then(resolve)
                .catch((error) => {
                    ServiceHelper.showError("Failed to update Surgery Vital.", undefined)
                    reject(error);
                });
        }), this.tags.upsertVisitAnesthesiaFormSurgeryVital)

    static upsertVisitAnesthesiaFormPostSurgeryVital = (visitId: number, anesthesiaMonitoringFormId: number, postSurgeryVital: VisitAnesthesiaFormPostSurgeryVitalModel): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(
            new Promise((resolve, reject) => {
                http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}/postsurgeryvital`)
                    .post(postSurgeryVital)
                    .then((data: IVisitAnesthesiaFormModel) => new VisitAnesthesiaFormModel(data))
                    .then(resolve)
                    .catch((error) => {
                        ServiceHelper.showError("Failed to update Post Surgery Vital.", undefined)
                        reject(error);
                    });
            }), this.tags.upsertVisitAnesthesiaFormPostSurgeryVital)

    static deleteAnesthesiaMedication = (visitId: number, anesthesiaMonitoringFormId: number, medicationId: number): Promise<VisitAnesthesiaFormModel> =>
        trackPromise(new Promise<VisitAnesthesiaFormModel>((resolve, reject) => {
            http(`${this.baseUrl}/${visitId}/${anesthesiaMonitoringFormId}/${medicationId}/preop`)
                .deleteWithResponse<IVisitAnesthesiaFormModel>()
                .then(data => new VisitAnesthesiaFormModel(data))
                .then(resolve)
                .catch(reject)
        }), this.tags.deleteMedication)




}
